import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import MAP from "../data/agentMemoryMap";
import "./AgentMemoryMap.css";

const LAYER_ORDER = ["interface", "harness", "training", "tooling"];
const COL_KEYS = ["problems", "solutions", "effects"];
const COL_TITLES = {
  problems: "Memory and context problems",
  solutions: "Solutions",
  effects: "Side effects and caveats",
};

function colOf(id) {
  if (id.startsWith("p_")) return "problems";
  if (id.startsWith("s_")) return "solutions";
  return "effects";
}

function solutionGroups(mode) {
  if (mode === "topic") return MAP.solutions;
  const buckets = {};
  LAYER_ORDER.forEach((l) => (buckets[l] = []));
  MAP.solutions.forEach((g) =>
    g.cards.forEach((c) => {
      const l = c.layer || "harness";
      (buckets[l] || (buckets[l] = [])).push(c);
    })
  );
  return Object.keys(buckets)
    .filter((l) => buckets[l].length)
    .map((l) => ({ group: l, cards: buckets[l] }));
}

function baseGroups(kind, mode) {
  if (kind === "solutions") return solutionGroups(mode);
  return MAP[kind];
}

function neighbours(id) {
  const set = new Set([id]);
  MAP.links.forEach(([a, b]) => {
    if (a === id) set.add(b);
    if (b === id) set.add(a);
  });
  if (id.startsWith("p_") || id.startsWith("e_")) {
    const first = Array.from(set);
    MAP.links.forEach(([a, b]) => {
      if (id.startsWith("p_") && a.startsWith("s_") && first.includes(a)) set.add(b);
      if (id.startsWith("e_") && b.startsWith("s_") && first.includes(b)) set.add(a);
    });
  }
  return set;
}

function Severity({ value }) {
  const sev = Math.min(3, Math.max(0, value | 0));
  if (!sev) return null;
  return (
    <span className="amm-sev" title={`impact on agent work: ${sev} of 3`}>
      {[1, 2, 3].map((n) => (
        <i key={n} className={n <= sev ? "on" : ""} />
      ))}
    </span>
  );
}

export default function AgentMemoryMap() {
  const [mode, setMode] = useState("topic");
  const [focusId, setFocusId] = useState(null);
  const [pinned, setPinned] = useState(null);

  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const cardRefs = useRef({});
  const connectedGroupRefs = useRef({});
  const leaveTimer = useRef(null);

  const lit = focusId ? neighbours(focusId) : null;

  const columns = COL_KEYS.map((kind) => {
    const groups = baseGroups(kind, mode);
    if (!focusId || colOf(focusId) === kind) return { kind, groups, sorted: false };
    const all = groups.flatMap((g) => g.cards);
    return {
      kind,
      sorted: true,
      groups: [
        { group: "connected", cards: all.filter((c) => lit.has(c.id)) },
        { group: "other", cards: all.filter((c) => !lit.has(c.id)) },
      ],
    };
  });

  const drawArrows = () => {
    const svg = svgRef.current;
    const stage = stageRef.current;
    if (!svg || !stage) return;
    const sr = stage.getBoundingClientRect();
    svg.setAttribute("width", stage.scrollWidth);
    svg.setAttribute("height", stage.scrollHeight);
    const parts = [];
    MAP.links.forEach(([a, b]) => {
      const A = cardRefs.current[a];
      const B = cardRefs.current[b];
      if (!A || !B) return;
      const ra = A.getBoundingClientRect();
      const rb = B.getBoundingClientRect();
      const x1 = ra.right - sr.left;
      const y1 = ra.top + ra.height / 2 - sr.top;
      const x2 = rb.left - sr.left;
      const y2 = rb.top + rb.height / 2 - sr.top;
      const dx = Math.max(40, (x2 - x1) * 0.5);
      const cls = "arrow " + (a.startsWith("p_") ? "ps" : "se") + (lit && lit.has(a) && lit.has(b) ? " lit" : "");
      parts.push(
        `<path class="${cls}" d="M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}" />`
      );
    });
    svg.innerHTML = parts.join("");
  };

  useLayoutEffect(() => {
    Object.values(connectedGroupRefs.current).forEach((g) => {
      if (g) g.style.marginTop = "";
    });
    if (focusId) {
      const hovered = cardRefs.current[focusId];
      if (hovered) {
        const hoveredTop = hovered.getBoundingClientRect().top;
        columns.forEach((col) => {
          if (!col.sorted) return;
          const first = col.groups[0].cards[0];
          const group = connectedGroupRefs.current[col.kind];
          const firstEl = first && cardRefs.current[first.id];
          if (group && firstEl) {
            const delta = hoveredTop - firstEl.getBoundingClientRect().top;
            if (delta > 0) group.style.marginTop = delta + "px";
          }
        });
      }
    }
    drawArrows();
  });

  useEffect(() => {
    const onResize = () => drawArrows();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const enter = (id) => {
    clearTimeout(leaveTimer.current);
    if (!pinned) setFocusId(id);
  };
  const leave = () => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      if (!pinned) setFocusId(null);
    }, 160);
  };
  const click = (e, id) => {
    e.stopPropagation();
    if (pinned === id) {
      setPinned(null);
      setFocusId(null);
    } else {
      setPinned(id);
      setFocusId(id);
    }
  };
  const clearPin = () => {
    setPinned(null);
    setFocusId(null);
  };
  const switchMode = (m) => {
    setMode(m);
    clearPin();
  };

  return (
    <div className="amm-page">
      <Helmet>
        <title>Current State of Agent Memory, September 2026 | Helia Jamshidi</title>
        <meta
          name="description"
          content="A map of memory and context problems in AI agents, the solutions being tried, and their side effects, drawn from a summer of reading."
        />
      </Helmet>
      <h1>Current State of Agent Memory</h1>
      <p className="amm-date">September 2026</p>
      <p>
        Over the summer I read through what shipped and what was published on memory for
        AI agents: the memory layers inside coding harnesses, the memory products, the
        benchmarks, and the papers that try to train memory into the model.
      </p>
      <p>
        Three columns. On the left, the problems, with a rough mark for how much each one
        hurts an agent at work. In the middle, the solutions, which you can group by topic
        or by where they live: the interface a person sees, the harness around the model,
        the model's training, or plain tooling. On the right, the side effects and caveats,
        because most of these solutions trade one problem for another.
      </p>
      <p>
        Hover a card to trace its links; click to pin. When you hover a problem, its
        solutions and their side effects move level with it so you can read the chain
        without following lines across the page.
      </p>

      <div className="amm-legend">
        <div className="amm-legend-group">
          <span className="amm-legend-label">Solutions</span>
          <div className="amm-toggle">
            <button className={mode === "topic" ? "on" : ""} onClick={() => switchMode("topic")}>
              by topic
            </button>
            <button className={mode === "layer" ? "on" : ""} onClick={() => switchMode("layer")}>
              by layer
            </button>
          </div>
        </div>
        <div className="amm-legend-group">
          <span className="amm-legend-label">Layers</span>
          {LAYER_ORDER.map((l) => (
            <span key={l} className={"amm-layer " + l}>
              {l}
            </span>
          ))}
        </div>
        <div className="amm-legend-group">
          <span className="amm-legend-label">Impact</span>
          <Severity value={1} /> low
          <Severity value={3} /> high
        </div>
        {pinned && (
          <button className="amm-clear" onClick={clearPin}>
            clear pin
          </button>
        )}
      </div>

      <div className="amm-scroll">
        <div
          ref={stageRef}
          className={"amm-stage" + (focusId ? " focusing" : "")}
          onClick={() => pinned && clearPin()}
        >
          <svg ref={svgRef} className="amm-arrows" />
          <div className="amm-columns">
            {columns.map((col) => (
              <div key={col.kind} className={"amm-col amm-col-" + col.kind}>
                <h2>{COL_TITLES[col.kind]}</h2>
                {col.groups.map((g, gi) => (
                  <div
                    key={g.group}
                    className="amm-group"
                    ref={(el) => {
                      if (col.sorted && gi === 0) connectedGroupRefs.current[col.kind] = el;
                    }}
                  >
                    <h3>{g.group}</h3>
                    {g.cards.map((c) => (
                      <div
                        key={c.id}
                        ref={(el) => {
                          cardRefs.current[c.id] = el;
                        }}
                        className={
                          "amm-card" +
                          (lit && lit.has(c.id) ? " lit" : "") +
                          (pinned === c.id ? " pinned" : "")
                        }
                        onMouseEnter={() => enter(c.id)}
                        onMouseLeave={leave}
                        onClick={(e) => click(e, c.id)}
                      >
                        <div className="amm-title">{c.title}</div>
                        <div className="amm-detail">{c.detail}</div>
                        {c.layer && <span className={"amm-layer " + c.layer}>{c.layer}</span>}
                        <Severity value={c.severity} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
